import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

import { MJERA_KRUPNO, MJERA_POLJA, MJERA_VISEREDNO, nativnoPolje } from './polje'

/**
 * Polja obrasca. Greška stoji UZ polje na koje se odnosi (zadatak 6b §5) — 422 s
 * poslužitelja nosi ime svojstva, pa poruka ne mora završiti u zajedničkom okviru
 * na vrhu ekrana, gdje korisnik ne zna što popraviti.
 */

/**
 * ZVJEZDICA JE OBEĆANJE, NE UKRAS (D15-A §3).
 *
 * `obavezno` je dotad crtalo samo zvjezdicu, a `required` se dopisivao rukom —
 * pa je obrazac mogao imati polje označeno kao obavezno koje submit mirno pusti
 * praznim. Zakon forme (`components/zakonForme.ts`) čita `validity`, dakle točno
 * ono što je zaboravljeno dopisati. Sada je jedno: što nosi zvjezdicu, to
 * preglednik i provjerava.
 *
 * Pozivatelj i dalje smije reći `required` izrijekom (polje bez zvjezdice koje
 * je ipak obavezno); zvjezdica ga samo podrazumijeva.
 */
export interface OkvirProps {
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
  children: (id: string, opisano: string | undefined) => ReactNode
}

/**
 * Natpis, podsjetnik i poruka oko kontrole — jedno mjesto za sve što polje čini
 * poljem. Izvezen je zbog vlastitog pickera (`components/datum/`): on kontrolu
 * crta sam, ali okvir mora biti ISTI, inače bi se datum na ekranu razlikovao od
 * polja pored sebe.
 */
export function Okvir({ label, hint, greska, obavezno, children }: OkvirProps) {
  const id = useId()
  const idHint = hint ? `${id}-hint` : undefined
  const idGreska = greska ? `${id}-greska` : undefined
  const opisano = [idHint, idGreska].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-semibold tracking-wide text-ink2 uppercase">
        {label}
        {obavezno ? <span className="text-brass"> *</span> : null}
      </label>

      {children(id, opisano)}

      {hint ? (
        <p id={idHint} className="text-xs text-ink2">
          {hint}
        </p>
      ) : null}

      {greska ? (
        <p id={idGreska} role="alert" className="text-xs font-medium text-bad-tx">
          {greska}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Boja i ponašanje su konzolini; MJERA dolazi iz zajedničkog izvora
 * (`components/polje.ts`), pa `<input>` i `<select>` jedan uz drugoga stoje na
 * istoj crti (D15-A §3).
 */
const TON = 'bg-card text-ink placeholder:text-ink2/60 focus:border-brass focus:outline-none ' +
  'disabled:bg-paper disabled:text-ink2'

function razred(krupno: boolean, greska: string | undefined, dodatno = ''): string {
  return [
    krupno ? MJERA_KRUPNO : MJERA_POLJA,
    TON,
    dodatno,
    greska ? 'border-bad-tx' : 'border-hair',
  ]
    .filter(Boolean)
    .join(' ')
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
  /** Polje kroz koje se BIRA (soba, termin, iznos) — veća meta i veće slovo. */
  krupno?: boolean
}

export function TextField({
  label,
  hint,
  greska,
  obavezno,
  krupno = false,
  ...ostalo
}: TextFieldProps) {
  // Datum i vrijeme traže vlastiti popravak (`components/polje.ts`) — isti onaj
  // koji dobiva i obrazac gosta, jer je nativno polje isto na obje strane.
  const nativno = nativnoPolje(ostalo.type)

  return (
    <Okvir label={label} hint={hint} greska={greska} obavezno={obavezno}>
      {(id, opisano) => (
        <input
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          lang={nativno.lang}
          required={obavezno}
          className={razred(krupno, greska, nativno.razred)}
          {...ostalo}
        />
      )}
    </Okvir>
  )
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
  krupno?: boolean
  children: ReactNode
}

export function SelectField({
  label,
  hint,
  greska,
  obavezno,
  krupno = false,
  children,
  ...ostalo
}: SelectFieldProps) {
  return (
    <Okvir label={label} hint={hint} greska={greska} obavezno={obavezno}>
      {(id, opisano) => (
        <select
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          required={obavezno}
          className={razred(krupno, greska)}
          {...ostalo}
        >
          {children}
        </select>
      )}
    </Okvir>
  )
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
}

/**
 * VIŠEREDNO POLJE (D15-A §3).
 *
 * Postoji zato što su ga ekrani dotad crtali sami: kontakt resorta, tekst
 * kampanje i odlomci ponude imali su tri ručno pisana `<textarea>`-a s tri
 * različita ruba i bez ijedne od stvari koje polje čine poljem — natpisa iznad,
 * poruke uz polje i `aria-describedby`-ja. Zakon forme
 * (`components/zakonForme.ts`) računa na to da svako polje ima natpis; polje koje
 * ga nema u popisu palih ostaje bezimeno.
 */
export function TextAreaField({
  label,
  hint,
  greska,
  obavezno,
  ...ostalo
}: TextAreaFieldProps) {
  return (
    <Okvir label={label} hint={hint} greska={greska} obavezno={obavezno}>
      {(id, opisano) => (
        <textarea
          id={id}
          aria-describedby={opisano}
          aria-invalid={greska ? true : undefined}
          required={obavezno}
          className={`${MJERA_VISEREDNO} ${TON} ${greska ? 'border-bad-tx' : 'border-hair'}`}
          {...ostalo}
        />
      )}
    </Okvir>
  )
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  hint?: string
  /**
   * Kvačica kojom se BIRA USLUGA, a ne postavka obrasca (krug 10, nalaz 5):
   * krakovi check-in/check-out paketa. Pult ih štiklira dok gost stoji nasuprot,
   * pa su meta i natpis krupniji — filtar "prikaži i neaktivne" to nije i ostaje
   * kakav jest.
   */
  krupno?: boolean
}

export function CheckboxField({ label, hint, id, krupno = false, ...ostalo }: CheckboxProps) {
  const vlastitiId = useId()
  const stvarniId = id ?? vlastitiId

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={stvarniId}
        className={`flex touch-target items-center gap-3 text-ink ${
          krupno ? 'min-h-12 text-base font-medium' : 'text-sm'
        }`}
      >
        <input
          id={stvarniId}
          type="checkbox"
          className={`shrink-0 accent-brass ${krupno ? 'size-6' : 'size-5'}`}
          {...ostalo}
        />
        {label}
      </label>
      {hint ? <p className={krupno ? 'text-sm text-ink2' : 'text-xs text-ink2'}>{hint}</p> : null}
    </div>
  )
}

interface BojaFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  hint?: string
  greska?: string
  /** Vrijednost ISPISANA uz birač — `#rrggbb`, za prepisivanje iz brand-booka. */
  value: string
}

/**
 * BIRAČ BOJE (V1A §3) — akcentna boja resorta.
 *
 * Vlastito polje kompleta, a ne goli `<input type="color">` u ekranu, iz istog
 * razloga kao i sva ostala (vidi `zakonPolja.test.ts`): natpis iznad, podsjetnik
 * ispod, `aria-describedby` i dodirna meta od 44 px moraju doći iz JEDNOG izvora.
 * Nativni birač je uz to jedna od kontrola koje preglednik crta sam, pa mu mjera
 * bez `appearance-none` bježi jednako kao datumu i satu.
 *
 * **Broj stoji UZ birač**, i to nije ukras: resort koji ima brand-book prepisuje
 * heksadekadsku vrijednost s papira i mora vidjeti poklapa li se. Ispisuje se, ne
 * upisuje — polje za tipkanje boje bilo bi četvrti način da se ista boja zapiše
 * krivo.
 */
export function BojaField({ label, hint, greska, value, ...ostalo }: BojaFieldProps) {
  return (
    <Okvir label={label} hint={hint} greska={greska}>
      {(id, opisano) => (
        <div className="flex items-center gap-3">
          <input
            id={id}
            type="color"
            value={value}
            aria-describedby={opisano}
            aria-invalid={greska ? true : undefined}
            className="touch-target h-11 w-16 cursor-pointer rounded-lg border border-hair bg-paper p-1"
            {...ostalo}
          />

          <span className="font-mono text-sm text-ink">{value}</span>
        </div>
      )}
    </Okvir>
  )
}
